/**
 * SecurityStrengthIndicator Component
 *
 * Displays a visual security strength indicator with 3 colored bars
 * based on Microsoft Entra authentication method security ratings.
 *
 * Features:
 * - Three horizontal bars showing security level (1-3)
 * - Color-coded: green (3 bars/High), yellow (2 bars/Medium), red (1 bar/Low)
 * - Security level text ("High security", "Medium security", "Low security")
 * - Compact, inline display suitable for method cards
 */

import { makeStyles, tokens, Text } from '@fluentui/react-components';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  barsContainer: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXS,
    alignItems: 'center',
  },
  bar: {
    width: '24px',
    height: '4px',
    borderRadius: tokens.borderRadiusSmall,
  },
  barActive: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
  },
  barActiveWarning: {
    backgroundColor: tokens.colorPaletteYellowBackground3,
  },
  barActiveDanger: {
    backgroundColor: tokens.colorPaletteRedBackground3,
  },
  barInactive: {
    backgroundColor: tokens.colorNeutralBackground4,
  },
  securityText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  securityTextStrong: {
    color: tokens.colorPaletteGreenForeground1,
  },
  securityTextWarning: {
    color: tokens.colorPaletteYellowForeground2,
  },
  securityTextDanger: {
    color: tokens.colorPaletteRedForeground1,
  },
});

interface SecurityStrengthIndicatorProps {
  /**
   * Security level from 1 (Low) to 3 (High) based on Microsoft Entra ratings
   */
  level: number;
  /**
   * Security level text to display (e.g., "High security", "Medium security", "Low security")
   */
  securityText: string;
}

export function SecurityStrengthIndicator({
  level,
  securityText,
}: SecurityStrengthIndicatorProps) {
  const styles = useStyles();

  // Determine bar color based on security level
  const getBarActiveClass = () => {
    if (level === 3) return styles.barActive; // Green for high security
    if (level === 2) return styles.barActiveWarning; // Yellow for medium security
    return styles.barActiveDanger; // Red for low security
  };

  // Determine text color based on security level
  const getTextColorClass = () => {
    if (level === 3) return styles.securityTextStrong; // Green text for high
    if (level === 2) return styles.securityTextWarning; // Yellow text for medium
    return styles.securityTextDanger; // Red text for low
  };

  const barActiveClass = getBarActiveClass();
  const textColorClass = getTextColorClass();

  return (
    <div className={styles.container}>
      <div className={styles.barsContainer} aria-label={`Security level ${level} out of 3`}>
        {[1, 2, 3].map((barNumber) => (
          <div
            key={barNumber}
            className={`${styles.bar} ${
              barNumber <= level ? barActiveClass : styles.barInactive
            }`}
            aria-hidden="true"
          />
        ))}
      </div>
      <Text className={`${styles.securityText} ${textColorClass}`}>
        {securityText}
      </Text>
    </div>
  );
}
